import React from "react";
import moment from "moment";
import TableTooltip from "./TableTooltip";
import TableActions from "./TableActions";

const getCurrency = () => localStorage.getItem("currencycode") || "₹";

const getKotCount = (table) => {
  if (typeof table?.kotCount === "number") return table.kotCount;
  if (Array.isArray(table?.kots)) return table.kots.length;
  return Number(table?.kots || 0) || 0;
};

const getStatusClass = (status) => {
  const normalized = String(status || "Available").toLowerCase();
  if (normalized === "occupied") return "occupied";
  if (normalized === "reserved") return "reserved";
  if (normalized === "dirty") return "dirty";
  return "available";
};

const getStatusLabel = (status, isMerged = false) => {
  const normalized = String(status || "Available").toLowerCase();

  if (isMerged) {
    if (normalized === "occupied") return "Merged Live";
    if (normalized === "reserved") return "Merged Reserved";
    if (normalized === "dirty") return "Merged Dirty";
    return "Merged Ready";
  }

  if (normalized === "occupied") return "Live";
  if (normalized === "reserved") return "Reserved";
  if (normalized === "dirty") return "Dirty";
  return "Ready";
};

const getSince = (table) => {
  const start = table?.occupied_at || table?.orderStart;
  if (!start) return null;

  const m = moment(start);
  if (!m.isValid()) return null;

  return moment.duration(moment().diff(m)).humanize();
};

const getGroupAmount = (group) =>
  group.reduce((sum, table) => sum + Number(table?.billAmount || 0), 0);

const formatMoney = (value) => {
  const currency = getCurrency();
  const n = Number(value || 0);
  return `${currency}${n.toLocaleString("en-IN", {
    minimumFractionDigits: n % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
};

const shortText = (value, max = 22) => {
  const text = String(value || "").trim();
  if (!text) return "—";
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

const getZone = (table) => table?.zone || table?.location || "No zone";

const TableCard = ({
  group,
  groupIndex,
  handleTableClick,
  handleAddKOT,
  handleUnmerge,
  handleOpenBillingPanel,
  mergeMode,
  selectedForMerge,
  getStatusColor,
}) => {
  if (!Array.isArray(group) || group.length === 0) {
    console.error("Invalid group prop:", group);
    return null;
  }

  const isMerged = group.length > 1;
  const primaryTable = group[0];
  const status = primaryTable?.status || "Available";
  const statusColor = getStatusColor?.(status) || "#64748b";
  const statusClass = getStatusClass(status);
  const totalKots = group.reduce((sum, table) => sum + getKotCount(table), 0);
  const totalCapacity = group.reduce((sum, table) => sum + Number(table?.capacity || 0), 0);
  const mergedId = group.map((table) => table?.name).filter(Boolean).join(", ");
  const groupIds = group.map((table) => table?.id).filter(Boolean);
  const isSelectedForMerge =
    mergeMode && group.some((table) => selectedForMerge?.includes(table.id));
  const sinceLabel = getSince(primaryTable);
  const groupAmount = getGroupAmount(group);

  const cardStyle = {
    "--table-status-color": statusColor,
    gridColumn: isMerged ? "span 2" : "span 1",
  };

  const renderCard = (table) => {
    const tableStatus = table?.status || "Available";
    const tableStatusColor = getStatusColor?.(tableStatus) || "#64748b";
    const tableStatusClass = getStatusClass(tableStatus);
    const tableKotCount = getKotCount(table);
    const tableSince = getSince(table);
    const tableAmount = Number(table?.billAmount || 0);
    const selected = mergeMode && selectedForMerge?.includes(table.id);
    const capacity = Number(table?.capacity || 0);

    return (
      <article
        key={table.id}
        id={`table-${table.id}`}
        className={`ns-table-card-inner ${tableStatusClass} ${selected ? "selected-for-merge" : ""}`}
        style={{ "--table-status-color": tableStatusColor }}
        onClick={() => handleTableClick?.(table)}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleTableClick?.(table);
          }
        }}
      >
        <div className="ns-table-card-top">
          <span className="ns-table-status-indicator" aria-hidden="true">
            <span className="ns-table-status-dot" />
            <span className="ns-table-status-text">{getStatusLabel(tableStatus)}</span>
          </span>

          {tableKotCount > 0 ? (
            <span className="ns-table-kot-badge">{tableKotCount} KOT</span>
          ) : (
            <span className="ns-table-kot-badge muted">No KOT</span>
          )}
        </div>

        <div className="ns-table-card-focus">
          <TableTooltip group={[table]} status={tableStatus} />
        </div>

        <div className="ns-table-metrics">
          <span>{capacity} seats</span>
          <span>{tableKotCount} KOT</span>
          {tableAmount > 0 ? <span>{formatMoney(tableAmount)}</span> : null}
        </div>

        {tableSince ? <div className="ns-table-time-chip">Since {tableSince}</div> : null}

        <TableActions
          status={tableStatus}
          group={[table]}
          groupIndex={groupIndex}
          handleTableClick={handleTableClick}
          handleAddKOT={handleAddKOT}
          handleUnmerge={() => handleUnmerge?.(groupIds)}
          handleOpenBillingPanel={handleOpenBillingPanel}
          isMerged={false}
          showBilling={tableKotCount > 0}
        />
      </article>
    );
  };

  return (
    <div
      key={`group-${groupIndex}`}
      id={isMerged ? `group-${groupIndex}` : `table-group-${primaryTable.id}`}
      className={`ns-table-card-shell ${isMerged ? "merged" : ""} ${statusClass} ${
        isSelectedForMerge ? "selected-for-merge" : ""
      }`}
      style={cardStyle}
    >
      <style>{tableCardStyles}</style>

      {isMerged ? (
        <article
          className={`ns-table-card-inner merged-card ${statusClass} ${
            isSelectedForMerge ? "selected-for-merge" : ""
          }`}
          style={{ "--table-status-color": statusColor }}
          onClick={() => handleTableClick?.(primaryTable)}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleTableClick?.(primaryTable);
            }
          }}
        >
          <div className="ns-table-card-top">
            <span className="ns-table-status-indicator" aria-hidden="true">
              <span className="ns-table-status-dot" />
              <span className="ns-table-status-text">{getStatusLabel(status, true)}</span>
            </span>

            <span className="ns-table-kot-badge">{group.length} tables</span>
          </div>

          <div className="ns-table-card-focus">
            <TableTooltip group={group} status={status} mergedId={mergedId} />
          </div>

          <div className="ns-table-merged-title" title={mergedId}>
            {shortText(mergedId, 46)}
          </div>

          <div className="ns-table-metrics merged">
            <span>{totalCapacity} seats</span>
            <span>{totalKots} KOT</span>
            {groupAmount > 0 ? <span>{formatMoney(groupAmount)}</span> : null}
          </div>

          {sinceLabel ? <div className="ns-table-time-chip">Since {sinceLabel}</div> : null}

          <TableActions
            status={status}
            group={group}
            groupIndex={groupIndex}
            handleTableClick={handleTableClick}
            handleAddKOT={handleAddKOT}
            handleUnmerge={() => handleUnmerge?.(groupIds)}
            handleOpenBillingPanel={handleOpenBillingPanel}
            isMerged={true}
            showBilling={totalKots > 0}
          />
        </article>
      ) : (
        group.map((table) => renderCard(table))
      )}
    </div>
  );
};

const tableCardStyles = `
  .ns-table-card-shell {
    min-width: 0;
  }

  .ns-table-card-shell.merged {
    min-width: 0;
  }

  .ns-table-card-inner {
    position: relative;
    min-height: 178px;
    height: 100%;
    border-radius: 18px;
    background:
      radial-gradient(circle at top left, color-mix(in srgb, var(--table-status-color) 10%, transparent), transparent 36%),
      linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-top: 4px solid var(--table-status-color);
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.055);
    padding: 11px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    cursor: pointer;
    overflow: hidden;
    transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
  }

  .ns-table-card-inner:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 38px rgba(15, 23, 42, 0.11);
    border-color: color-mix(in srgb, var(--table-status-color) 36%, rgba(15, 23, 42, 0.08));
  }

  .ns-table-card-inner:focus-visible {
    outline: 3px solid rgba(37, 99, 235, 0.18);
    outline-offset: 3px;
  }

  .ns-table-card-inner.selected-for-merge {
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.22), 0 18px 38px rgba(15, 23, 42, 0.12);
    border-color: #2563eb;
  }

  .ns-table-card-inner.merged-card {
    min-height: 194px;
    background:
      radial-gradient(circle at top left, color-mix(in srgb, var(--table-status-color) 14%, transparent), transparent 36%),
      linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  }

  .ns-table-card-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    min-height: 27px;
  }

  .ns-table-status-indicator {
    min-width: 0;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: #334155;
    font-size: 0.72rem;
    font-weight: 950;
    line-height: 1;
  }

  .ns-table-status-dot {
    width: 11px;
    height: 11px;
    border-radius: 999px;
    background: var(--table-status-color);
    box-shadow: 0 0 0 5px color-mix(in srgb, var(--table-status-color) 14%, transparent);
    flex: 0 0 auto;
  }

  .ns-table-card-inner.occupied .ns-table-status-dot {
    animation: nsTablePulse 1.8s ease-in-out infinite;
  }

  .ns-table-status-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ns-table-kot-badge {
    min-height: 24px;
    border-radius: 999px;
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
    padding: 4px 8px;
    font-size: 0.68rem;
    font-weight: 950;
    white-space: nowrap;
    flex: 0 0 auto;
  }

  .ns-table-kot-badge.muted {
    background: #f8fafc;
    color: #64748b;
    border-color: rgba(15, 23, 42, 0.08);
  }

  .ns-table-card-focus {
    min-width: 0;
    flex: 1 1 auto;
  }

  .ns-table-tooltip-trigger {
    min-width: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 6px;
    text-align: left;
  }

  .ns-table-title-row {
    min-width: 0;
    max-width: 100%;
  }

  .ns-table-name {
    display: block;
    max-width: 100%;
    color: #001f5b;
    font-size: 1.16rem;
    font-weight: 950;
    line-height: 1.1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .merged-card .ns-table-name {
    font-size: 1rem;
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .ns-table-zone {
    max-width: 100%;
    color: #64748b;
    font-size: 0.72rem;
    font-weight: 850;
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  .ns-table-zone span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ns-table-mini-stats {
    display: none;
  }

  .ns-table-capacity-dots {
    display: none;
  }

  .ns-table-since {
    display: none;
  }

  .ns-table-metrics,
  .ns-table-merged-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .ns-table-metrics span,
  .ns-table-merged-meta span {
    min-height: 24px;
    border-radius: 999px;
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.08);
    color: #475569;
    padding: 4px 7px;
    font-size: 0.68rem;
    font-weight: 900;
    display: inline-flex;
    align-items: center;
  }

  .ns-table-merged-title {
    color: #64748b;
    font-size: 0.72rem;
    font-weight: 850;
    line-height: 1.3;
    margin-top: -2px;
  }

  .ns-table-time-chip {
    width: fit-content;
    min-height: 24px;
    border-radius: 999px;
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 4px 8px;
    font-size: 0.68rem;
    font-weight: 950;
  }

  .ns-table-actions {
    margin-top: auto;
    display: flex;
    justify-content: flex-start;
    gap: 7px;
    flex-wrap: wrap;
    position: relative;
    z-index: 2;
  }

  .ns-table-action-btn {
    min-height: 32px;
    border-radius: 12px;
    border: 0;
    padding: 0 10px;
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 0.72rem;
    font-weight: 950;
    cursor: pointer;
    transition: transform 0.14s ease, box-shadow 0.14s ease, background 0.14s ease;
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.10);
  }

  .ns-table-action-btn:hover {
    transform: translateY(-1px);
  }

  .ns-table-action-btn.primary {
    background: #1d4ed8;
  }

  .ns-table-action-btn.success {
    background: #16a34a;
  }

  .ns-table-action-btn.danger {
    background: #dc2626;
  }

  .ns-table-action-btn.warning {
    background: #f97316;
  }

  .ns-table-unmerge-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 34px;
    height: 34px;
    border-radius: 13px;
    border: 1px solid rgba(220, 38, 38, 0.18);
    background: #fef2f2;
    color: #b91c1c;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 4;
    box-shadow: 0 10px 22px rgba(220, 38, 38, 0.12);
    transition: transform 0.14s ease, background 0.14s ease;
  }

  .ns-table-unmerge-btn:hover {
    transform: translateY(-1px);
    background: #fee2e2;
  }

  .ns-table-tooltip-content {
    min-width: 210px;
  }

  .ns-table-tooltip-title {
    color: #ffffff;
    font-weight: 950;
    margin-bottom: 8px;
  }

  .ns-table-tooltip-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 3px 0;
    font-size: 0.78rem;
  }

  .ns-table-tooltip-row strong {
    color: rgba(255,255,255,0.76);
  }

  .ns-table-tooltip-row span {
    color: #ffffff;
    text-align: right;
  }

  @keyframes nsTablePulse {
    0% { box-shadow: 0 0 0 4px color-mix(in srgb, var(--table-status-color) 14%, transparent); }
    50% { box-shadow: 0 0 0 8px color-mix(in srgb, var(--table-status-color) 22%, transparent); }
    100% { box-shadow: 0 0 0 4px color-mix(in srgb, var(--table-status-color) 14%, transparent); }
  }

  @supports not (background: color-mix(in srgb, red 10%, transparent)) {
    .ns-table-card-inner {
      background: #ffffff;
    }

    .ns-table-status-dot {
      box-shadow: 0 0 0 5px rgba(37, 99, 235, 0.10);
    }
  }

  @media (max-width: 760px) {
    .ns-table-card-shell.merged {
      grid-column: span 1 !important;
    }

    .ns-table-card-inner,
    .ns-table-card-inner.merged-card {
      min-height: 170px;
    }

    .ns-table-action-btn span {
      display: none;
    }

    .ns-table-action-btn {
      width: 34px;
      padding: 0;
    }
  }
`;

export default TableCard;
